import DEFAULT_SCHEMA from './defaultSchema';
import THEME3_SCHEMA from './theme3Schema';

// ─── Page-content schema registry ────────────────────────────────────────
//
// The Pages tab renders whichever schema the institute's active layout
// preset points at. 'default' and 'theme-2' share one schema because theme-2
// restyles the default page bodies rather than restructuring them; theme-3 is
// a different layout with different copy, so it has its own.
//
// Keys are namespaced per schema (theme-3 uses `t3_*`) so two schemas can hold
// content simultaneously. That is what lets an institute switch themes, look
// at it, and switch back without losing what they wrote.
//
// Mirror of vc_be/controllers/cmsAdminController.js → PAGE_KEYS. A key missing
// there is silently dropped on save, so the two lists must agree. The Header &
// Footer tab writes `global` directly; it is in PAGE_KEYS but in no schema.

const SCHEMAS = {
  default: DEFAULT_SCHEMA,
  'theme-2': DEFAULT_SCHEMA,
  'theme-3': THEME3_SCHEMA,
};

// Where each theme-3 page lives on the website, for its "View page" link.
// The default schema carries `path` on each page instead.
const T3_PATHS = {
  t3_home: '/',
  t3_courses: '/courses',
  t3_blogs: '/blogs',
  t3_contact: '/contact',
  t3_placements: '/placements',
};

const forLayout = (item, preset) => !item.layouts || item.layouts.includes(preset);

/**
 * The schema as the given layout renders it: sections and fields marked with
 * `layouts` that do not include this preset are dropped, and a section's
 * show/hide toggle is kept only where that layout honours it.
 */
export function resolveSchemaForLayout(schema, preset) {
  return schema.map((page) => ({
    ...page,
    path: page.path ?? T3_PATHS[page.pageKey] ?? null,
    sections: page.sections
      .filter((section) => forLayout(section, preset))
      .map((section) => {
        const visibility = section.visibility && forLayout(section.visibility, preset)
          ? section.visibility
          : null;
        return { ...section, visibility, fields: section.fields.filter((f) => forLayout(f, preset)) };
      })
      .filter((section) => section.fields.length > 0),
  }));
}

/** Always returns a schema — an unknown or stale preset falls back to default. */
export function getSchemaForPreset(preset) {
  const known = SCHEMAS[preset] ? preset : 'default';
  return resolveSchemaForLayout(SCHEMAS[known], known);
}

/** Every page key any schema can write, deduplicated. */
export const ALL_PAGE_KEYS = Array.from(
  new Set(Object.values(SCHEMAS).flatMap((s) => s.map((p) => p.pageKey)))
);

export { DEFAULT_SCHEMA, THEME3_SCHEMA };
export default SCHEMAS;
